import React, { useEffect, useState } from 'react';
import { BackButton } from '../ui';
import DiceBackground from '../DiceBackground';
import { supabase } from '../../utils/supabase';
import { useTranslation } from '../../i18n';

interface LeaderboardEntry {
  id: string;
  name: string;
  level: number;
  exp: number;
  wins: number;
  losses: number;
  draws: number;
}

interface LeaderboardProps {
  currentPlayerName: string;
  onBack: () => void;
}

export const Leaderboard: React.FC<LeaderboardProps> = ({ currentPlayerName, onBack }) => {
  const t = useTranslation();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('players')
        .select('id, name, level, exp, wins, losses, draws')
        .order('level', { ascending: false })
        .order('exp', { ascending: false })
        .limit(50);

      if (error) {
        console.error('[Leaderboard] fetch error:', error.message);
      } else {
        setEntries(data || []);
      }
      setIsLoading(false);
    };

    fetchLeaderboard();
  }, []);

  const getRankDisplay = (rank: number) => {
    if (rank === 1) return { label: t.rankFirst, color: 'text-yellow-400', bg: 'bg-yellow-900/30 border-yellow-600/50' };
    if (rank === 2) return { label: t.rankSecond, color: 'text-gray-300', bg: 'bg-gray-700/30 border-gray-500/50' };
    if (rank === 3) return { label: t.rankThird, color: 'text-orange-400', bg: 'bg-orange-900/30 border-orange-600/50' };
    return { label: `${rank}`, color: 'text-gray-500', bg: 'bg-black/20 border-gray-700/50' };
  };

  return (
    <div className="h-[100dvh] flex flex-col items-center justify-start text-white p-4 pt-8 relative select-none bg-[#1a101f] overflow-hidden">
      <DiceBackground count={10} />
      <BackButton onClick={onBack} />

      <h1 className="text-2xl lg:text-5xl font-bold tracking-tighter mb-6 text-theme drop-shadow-lg uppercase text-center relative z-10 mt-4">
        {t.leaderboard}
      </h1>

      {/* Column headers */}
      <div className="w-full max-w-2xl px-2 mb-2 relative z-10">
        <div className="flex items-center text-[8px] lg:text-[10px] text-gray-400 uppercase tracking-wider font-bold px-3">
          <span className="w-8 lg:w-12 text-center shrink-0">#</span>
          <span className="flex-1 ml-2">{t.player}</span>
          <span className="w-10 lg:w-14 text-center shrink-0">{t.lvl}</span>
          <span className="w-14 lg:w-20 text-center shrink-0">{t.exp}</span>
          <span className="w-16 lg:w-24 text-center shrink-0">{t.wld}</span>
        </div>
      </div>

      <div className="w-full max-w-2xl flex-1 flex flex-col gap-1.5 overflow-y-auto pr-2 custom-scrollbar relative z-10 pb-4">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500 text-sm animate-pulse uppercase tracking-widest">
            {t.loadingDots}
          </div>
        ) : entries.length === 0 ? (
          <div className="p-8 pixel-border text-center text-gray-500 text-sm italic uppercase">
            {t.noPlayers}
          </div>
        ) : (
          entries.map((entry, index) => {
            const rank = index + 1;
            const { label, color, bg } = getRankDisplay(rank);
            const isMe = entry.name === currentPlayerName;

            return (
              <div
                key={entry.id}
                className={`flex items-center px-3 py-2 lg:py-3 pixel-border transition-all ${bg} ${
                  isMe ? 'ring-2 ring-theme/60 shadow-lg shadow-theme/10' : ''
                }`}
              >
                {/* Rank */}
                <span className={`w-8 lg:w-12 text-center text-[9px] lg:text-sm font-bold shrink-0 ${color}`}>
                  {label}
                </span>

                {/* Name */}
                <div className="flex-1 ml-2 min-w-0">
                  <span className={`text-[9px] lg:text-sm font-bold truncate block ${isMe ? 'text-theme' : 'text-white'}`}>
                    {entry.name}
                    {isMe && (
                      <span className="text-[8px] lg:text-[9px] text-theme/70 ml-1 font-normal">{t.you}</span>
                    )}
                  </span>
                </div>

                {/* Level */}
                <span className="w-10 lg:w-14 text-center text-[8px] lg:text-xs font-bold text-theme shrink-0">
                  {entry.level}
                </span>

                {/* EXP */}
                <span className="w-14 lg:w-20 text-center text-[8px] lg:text-xs font-mono text-yellow-500 shrink-0">
                  {entry.exp.toLocaleString()}
                </span>

                {/* W/L/D */}
                <span className="w-16 lg:w-24 text-center text-[8px] lg:text-[10px] font-mono shrink-0">
                  <span className="text-green-400">{entry.wins}</span>
                  <span className="text-gray-500"> / </span>
                  <span className="text-red-400">{entry.losses}</span>
                  <span className="text-gray-500"> / </span>
                  <span className="text-gray-400">{entry.draws}</span>
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
